import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 60);
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(maxScroll > 0 ? (scrollY / maxScroll) * 100 : 0);
    };
    handleScroll(); // estado correto imediatamente no mount
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuItems = [
    { href: "#o-problema", label: "O Problema" },
    { href: "#nossa-solucao", label: "Nossa Solução" },
    { href: "#como-funciona", label: "Como Funciona" },
    { href: "#servicos", label: "Serviços" },
    { href: "#clientes", label: "Clientes" },
    { href: "#equipe", label: "Equipe" },
    { href: "#faq", label: "FAQ" },
  ];

  return (
    <>
      {/* Scroll progress bar */}
      <div
        className="fixed top-0 left-0 z-[60] h-[2px] pointer-events-none"
        style={{
          width: `${progress}%`,
          background: "var(--color-dourado)",
          opacity: progress > 2 ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}
      />

      {/* Mobile: fundo sempre escuro via Tailwind. Desktop: controlado pelo scroll via style */}
      <header
        className="fixed top-0 w-full z-50 transition-all duration-500 bg-[rgba(10,10,10,0.92)] [backdrop-filter:blur(12px)] [-webkit-backdrop-filter:blur(12px)] lg:bg-transparent lg:[backdrop-filter:none] lg:[-webkit-backdrop-filter:none]"
        style={scrolled ? {
          background: "rgba(10, 10, 10, 0.92)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 1px 0 0 rgba(242, 240, 236, 0.05), 0 4px 24px 0 rgba(0,0,0,0.45)",
        } : undefined}
      >
        <div className="container mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="#inicio" className="flex-shrink-0 cursor-pointer">
              <img
                src="/logo-branco.svg"
                alt="SIATECH"
                className="h-9 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-7">
              {menuItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="nav-link"
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-4">
              <a href="#contato" className="btn-outline-dark">
                Análise Gratuita
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 cursor-pointer"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
              style={{ color: "var(--color-branco-vela)" }}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

      </header>

      {/* Mobile Navigation - Tela cheia (fora do header para evitar conflito de stacking context) */}
      <div
        className="lg:hidden fixed inset-0 z-[55] transition-all duration-300"
        style={{
          background: "var(--color-preto-marca)",
          opacity: isMenuOpen ? 1 : 0,
          pointerEvents: isMenuOpen ? "auto" : "none",
        }}
      >
        {/* Botão fechar */}
        <button
          onClick={() => setIsMenuOpen(false)}
          aria-label="Fechar menu"
          className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-200"
          style={{
            borderRadius: "50%",
            border: "1px solid rgba(176, 175, 172, 0.3)",
            color: "var(--color-cinza-nevoa)",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = "var(--color-dourado)";
            (e.currentTarget as HTMLElement).style.color = "var(--color-dourado)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = "rgba(176, 175, 172, 0.3)";
            (e.currentTarget as HTMLElement).style.color = "var(--color-cinza-nevoa)";
          }}
        >
          <X size={16} />
        </button>

        <nav className="flex flex-col px-6 pt-24 pb-28 overflow-y-auto h-full">
          {menuItems.map((item, index) => (
            <a
              key={item.href}
              href={item.href}
              className="py-4 text-lg font-medium transition-all duration-300 flex items-center justify-between"
              style={{
                color: "var(--color-branco-vela)",
                borderBottom: "1px solid rgba(176, 175, 172, 0.08)",
                opacity: isMenuOpen ? 1 : 0,
                transform: isMenuOpen ? "translateX(0)" : "translateX(-16px)",
                transitionDelay: isMenuOpen ? `${index * 45}ms` : "0ms",
              }}
              onClick={() => setIsMenuOpen(false)}
              onTouchStart={(e) =>
                ((e.currentTarget as HTMLElement).style.color = "var(--color-dourado)")
              }
              onTouchEnd={(e) =>
                ((e.currentTarget as HTMLElement).style.color = "var(--color-branco-vela)")
              }
            >
              {item.label}
              <span style={{ color: "var(--color-cinza-ancora)", fontSize: "10px" }}>→</span>
            </a>
          ))}

          <a
            href="#contato"
            className="btn-cta-dark w-full mt-8 transition-all duration-300"
            style={{
              opacity: isMenuOpen ? 1 : 0,
              transform: isMenuOpen ? "translateY(0)" : "translateY(12px)",
              transitionDelay: isMenuOpen ? `${menuItems.length * 45}ms` : "0ms",
            }}
            onClick={() => setIsMenuOpen(false)}
          >
            Análise Gratuita
          </a>
        </nav>
      </div>

      {/* Mobile Bottom CTA Bar */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 px-4 py-3 transition-all duration-300"
        style={{
          background: "rgba(10, 10, 10, 0.92)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderTop: "1px solid rgba(176, 175, 172, 0.1)",
          opacity: isMenuOpen ? 0 : 1,
          pointerEvents: isMenuOpen ? "none" : "auto",
        }}
      >
        <a
          href="#contato"
          className="btn-cta-dark w-full text-sm"
        >
          Análise Gratuita
        </a>
      </div>
    </>
  );
};

export default Header;
